/**
 * Deterministic hashing shared by the editor, the compiler, the game client and the game server.
 *
 * Every value that must agree across those environments (autotile selection, rule probabilities, combat
 * rolls) is derived from `hash` with explicit integer inputs, never from `Math.random()`. The algorithm is
 * xxHash32 over the inputs encoded as little-endian 32-bit words, computed with `Math.imul` and unsigned
 * shifts so the result is bit-identical on every JavaScript engine.
 */

const PRIME_1 = 0x9e3779b1;
const PRIME_2 = 0x85ebca77;
const PRIME_3 = 0xc2b2ae3d;
const PRIME_4 = 0x27d4eb2f;
const PRIME_5 = 0x165667b1;
const WORDS_PER_STRIPE = 4;
const BYTES_PER_WORD = 4;
const UNIT_FLOAT_BITS = 24;
/**
 * The divisor that turns a 24-bit hash into a unit float (`toUnitFloat`, `unitHash`); a caller that divides `hashBits24` itself uses it.
 */
export const UNIT_FLOAT_DIVISOR = 2 ** UNIT_FLOAT_BITS;

/**
 * Rotates a 32-bit value left.
 *
 * @param value Unsigned 32-bit integer.
 * @param bits Rotation amount in the range 1 to 31.
 * @returns Rotated unsigned 32-bit integer.
 */
function rotateLeft(value: number, bits: number): number {
  return ((value << bits) | (value >>> (32 - bits))) >>> 0;
}

/**
 * Throws unless a value is an integer in the unsigned 32-bit range.
 *
 * @param value Value to check.
 * @param label Name used in the error message; a function of the index when the name is built from it, so that
 *   a value that passes costs no string.
 * @param index The value's index, given to `label` when it is a function.
 * @throws RangeError when the value is not an integer in `[0, 2^32)`.
 */
function assertUint32(value: number, label: string | ((index: number) => string), index = 0): void {
  if (!Number.isInteger(value) || value < 0 || value > 0xffffffff) {
    throw new RangeError(`${typeof label === "string" ? label : label(index)} must be an integer in [0, 2^32), received ${String(value)}`);
  }
}

/**
 * The name of a hashed word in an error message.
 *
 * @param index The word's index.
 * @returns The name.
 */
function inputName(index: number): string {
  return `input ${index}`;
}

/**
 * One xxHash32 lane step: mixes a word into an accumulator.
 *
 * @param accumulator Lane accumulator.
 * @param word Input word.
 * @returns Updated accumulator as an unsigned 32-bit integer.
 */
function round(accumulator: number, word: number): number {
  const mixed = (accumulator + Math.imul(word, PRIME_2)) >>> 0;
  return Math.imul(rotateLeft(mixed, 13), PRIME_1) >>> 0;
}

/**
 * Hashes the first `count` words of a buffer with xxHash32, treating each word as four little-endian bytes.
 * This form allocates nothing and is the one to use on hot paths with a preallocated buffer.
 *
 * @param seed Unsigned 32-bit seed.
 * @param words Buffer of unsigned 32-bit words.
 * @param count Number of leading words to hash.
 * @returns Unsigned 32-bit digest.
 * @throws RangeError when the seed or any hashed word is outside `[0, 2^32)`, or `count` exceeds the buffer.
 */
export function hashWords(seed: number, words: ArrayLike<number>, count: number): number {
  assertUint32(seed, "seed");
  if (!Number.isInteger(count) || count < 0 || count > words.length) {
    throw new RangeError(`count must be an integer in [0, ${words.length}], received ${String(count)}`);
  }
  for (let i = 0; i < count; i += 1) {
    assertUint32(words[i] as number, inputName, i);
  }
  let index = 0;
  let digest: number;
  if (count >= WORDS_PER_STRIPE) {
    let lane1 = (seed + PRIME_1 + PRIME_2) >>> 0;
    let lane2 = (seed + PRIME_2) >>> 0;
    let lane3 = seed >>> 0;
    let lane4 = (seed - PRIME_1) >>> 0;
    while (index + WORDS_PER_STRIPE <= count) {
      lane1 = round(lane1, words[index] as number);
      lane2 = round(lane2, words[index + 1] as number);
      lane3 = round(lane3, words[index + 2] as number);
      lane4 = round(lane4, words[index + 3] as number);
      index += WORDS_PER_STRIPE;
    }
    digest = (rotateLeft(lane1, 1) + rotateLeft(lane2, 7) + rotateLeft(lane3, 12) + rotateLeft(lane4, 18)) >>> 0;
  } else {
    digest = (seed + PRIME_5) >>> 0;
  }
  digest = (digest + count * BYTES_PER_WORD) >>> 0;
  while (index < count) {
    digest = (digest + Math.imul(words[index] as number, PRIME_3)) >>> 0;
    digest = Math.imul(rotateLeft(digest, 17), PRIME_4) >>> 0;
    index += 1;
  }
  digest ^= digest >>> 15;
  digest = Math.imul(digest, PRIME_2) >>> 0;
  digest ^= digest >>> 13;
  digest = Math.imul(digest, PRIME_3) >>> 0;
  digest ^= digest >>> 16;
  return digest >>> 0;
}

/**
 * Hashes a seed and any number of unsigned 32-bit inputs with xxHash32.
 *
 * Callers pass the coordinates that identify a decision, for example
 * `hash(seed, x, y, layerIndex, stageIndex, ruleId)` for a rule probability check. Use `hashWords` with a
 * reused buffer where allocation per call matters.
 *
 * @param seed Unsigned 32-bit seed.
 * @param inputs Unsigned 32-bit integers identifying the decision.
 * @returns Unsigned 32-bit digest.
 * @throws RangeError when the seed or any input is outside `[0, 2^32)`.
 */
export function hash(seed: number, ...inputs: number[]): number {
  return hashWords(seed, inputs, inputs.length);
}

/**
 * Maps an unsigned 32-bit digest to a float in `[0, 1)` using its top 24 bits. Twenty-four bits is the
 * float32 mantissa width, so the result survives a round trip through `Float32Array` or a shader uniform
 * unchanged, and 16 million distinct values is ample resolution for any probability check.
 *
 * @param value Unsigned 32-bit integer, typically a `hash` result.
 * @returns A number in `[0, 1)` with 24 bits of precision.
 * @throws RangeError when the value is outside `[0, 2^32)`.
 */
export function toUnitFloat(value: number): number {
  assertUint32(value, "value");
  return (value >>> (32 - UNIT_FLOAT_BITS)) / UNIT_FLOAT_DIVISOR;
}

/**
 * `toUnitFloat(hashWords(seed, [input], 1))` for a single word, worked in 32-bit integer arithmetic so neither the digest
 * nor anything on the way is ever a number outside the engine's small-integer range: a hot path that hashes per light per
 * frame allocates nothing (a digest of 2^31 or more returned from `hashWords` is a boxed number in V8). The same value as
 * the general form, bit for bit.
 *
 * @param seed Unsigned 32-bit seed; not checked, the caller passes one.
 * @param input Unsigned 32-bit input; not checked.
 * @returns A number in `[0, 1)` with 24 bits of precision.
 */
export function unitHash(seed: number, input: number): number {
  return hashBits24(seed, input) / UNIT_FLOAT_DIVISOR;
}

/**
 * The top 24 bits of `hashWords(seed, [input], 1)` as a whole number below 2^24: always a small integer, so a caller on a
 * hot path that divides by 2^24 itself allocates nothing even where this function is not inlined.
 *
 * @param seed Unsigned 32-bit seed; not checked.
 * @param input Unsigned 32-bit input; not checked.
 * @returns A whole number in `[0, 2^24)`.
 */
export function hashBits24(seed: number, input: number): number {
  let digest = (seed + PRIME_5 + BYTES_PER_WORD) | 0;
  digest = (digest + Math.imul(input, PRIME_3)) | 0;
  digest = Math.imul((digest << 17) | (digest >>> 15), PRIME_4);
  digest ^= digest >>> 15;
  digest = Math.imul(digest, PRIME_2);
  digest ^= digest >>> 13;
  digest = Math.imul(digest, PRIME_3);
  digest ^= digest >>> 16;
  return digest >>> (32 - UNIT_FLOAT_BITS);
}
